"use client";

import { useEffect, useSyncExternalStore } from "react";
import Script from "next/script";
import {
  useEpisodeLikedUser,
  useReadonlyLocalStorageStore,
  useSeriesFavorite,
  useStaticStore,
  useToast,
  useUserInfo,
} from "app/hooks";
import { getReadEpKey, STORE_KEYS } from "app/constants";
import { HEADER_HIDDEN_CLS } from "app/(app)/(common)/components/HeaderInteractions";
import { EpisodeModel } from "@api/models";
import { GLOBALS } from "config";
import { useRouter } from "next/navigation";
import { logError } from "@lib/logger";
import { sessionStore } from "@lib/sessionStore";
import { controlMissionToast } from "@lib/utils";

interface ComiciViewerProps {
  viewerId: string;
  episode: EpisodeModel;
}

export function ComiciViewer({ viewerId, episode }: ComiciViewerProps) {
  const { session, sessionStatus } = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.getSnapshot,
    sessionStore.getServerSnapshot,
  );
  const { userInfo, isLoading: isUserInfoLoading } = useUserInfo(sessionStatus === "authenticated");
  const router = useRouter();
  const { pushToast } = useToast();
  const { data: isViewerJSLoaded, mutate: setIsViewerJSLoaded } = useStaticStore<boolean>(
    STORE_KEYS.isViewerJsLoaded,
    false,
  );
  const { results: faved, isLoading: isFavedLoading } = useSeriesFavorite(
    episode.series.id,
    sessionStatus === "authenticated",
  );
  const { results: like, isLoading: isLikeLoading } = useEpisodeLikedUser(
    episode.id,
    sessionStatus === "authenticated",
  );

  // 縦読み/横読み/拡大表示の設定取得
  const { data: glimpseBottom, mutate: updateGlimplseBottom } =
    useReadonlyLocalStorageStore<string>("glimpse_bottom", "");
  const { data: scrollVertical, mutate: updateScrollVertical } =
    useReadonlyLocalStorageStore<string>("scroll_vertical", "");

  // Comici Viewerのロード
  useEffect(() => {
    updateGlimplseBottom();
    updateScrollVertical();
    if (
      isViewerJSLoaded &&
      !isUserInfoLoading &&
      !isFavedLoading &&
      !isLikeLoading &&
      sessionStatus !== "loading"
    ) {
      //pr枠等を設定
      window.comiciViewer = window.comiciViewer || {};
      window.comiciViewer.data = window.comiciViewer.data || {};
      window.comiciViewer.data.comicInfo = episode.viewer?.dataComicInfo;
      window.comiciViewer.data.bookInfo = {
        result: {
          authors: [{ name: "" }], //※ TODO:著者は使ってないはずだがviewer側要確認
          thumb_image_url: episode.summary.thumbnailImages?.at(0)?.url || "",
          title: episode.summary.title,
        },
      };
      window.comiciViewer.data.topPr = {
        banners: episode.viewer?.viewerBanner?.filter((item) => item.type === 1),
      };
      window.comiciViewer.data.pr = {
        banners: episode.viewer?.viewerBanner?.filter((item) => item.type === 2),
      };
      window.comiciViewer.data.pr2 = {
        banners: episode.viewer?.viewerBanner?.filter((item) => item.type === 3),
      };
      window.comiciViewer.data.pr3 = {
        banners: episode.viewer?.viewerBanner?.filter((item) => item.type === 4),
      };

      window.comiciViewer.init(true).catch((err) => {
        if (err.shouldReload) {
          window.location.reload();
          return;
        }
        logError("viewer error: " + err.message, {});
        pushToast(
          "viewerの読み込みでエラーが発生しました。しばらくお待ちいただいて、画面をリロードください",
        );
      });

      window.comiciViewer.onGoodButtonClick = (num: number) => {
        return new Promise((resolved, rejected) => {
          const episodeId = location.pathname.split("/").at(-1);
          fetch(`/api/episodes/${episodeId}/like`, { method: "post" }).then((res) => {
            if (res.status === 200) {
              resolved("ok");
            } else {
              rejected("NG");
            }
          });
        });
      };

      const sentEpisodeId: string[] = [];
      const readEpisodeId: string[] = [];
      window.comiciViewer.onPageView = (data) => {
        if (data.current > 10 || data.current >= data.total) {
          //読了カウント
          if (!sentEpisodeId.includes(episode.id)) {
            //TODO:失敗時のリカバリ
            fetch(`/api/episodes/${episode.id}/readCompleted`, { method: "POST" })
              .then(async (res) => {
                controlMissionToast((await res.json()).missions, pushToast);
              })
              .catch((err) => {
                //エラー時は保存したepisode.idを削除しておく
                logError("viewer readCompleted error: " + err.message, {});
                sentEpisodeId.splice(sentEpisodeId.indexOf(episode.id), 1);
              });

            //作品ごとの既読話を保存（非ログイン時の作品TOP既読位置反映用）
            const readEpKey = getReadEpKey(episode.series.id);
            const seriesEpisodeNumber = episode.seriesEpisodeNumber;
            if (readEpKey && seriesEpisodeNumber) {
              let currentReadEpNumberInt = 0;
              const currentReadEpNumberFromStorage = window.localStorage.getItem(readEpKey);
              if (currentReadEpNumberFromStorage) {
                currentReadEpNumberInt = parseInt(currentReadEpNumberFromStorage, 10);
              }
              if (seriesEpisodeNumber > currentReadEpNumberInt) {
                window.localStorage.setItem(readEpKey, String(seriesEpisodeNumber));
              }
            }
            //何度も送らないようにepisode idを保存
            sentEpisodeId.push(episode.id);
          }
        }
      };

      window.comiciViewer.onPostClick = () => {
        console.log("x share button clicked");
        fetch(`/api/series/${episode.series.id}/share`, {
          method: "POST",
        })
          .then(async (res) => {
            controlMissionToast(await res.json(), pushToast);
          })
          .catch(() => {
            logError("server error: series share");
          });
      };
      window.comiciViewer.onNext = () => {
        if (episode.nextEpisodeId) {
          router.push(`/episodes/${episode.nextEpisodeId}`);
        }
      };
      window.comiciViewer.onPrev = () => {
        if (episode.previousEpisodeId) {
          router.push(`/episodes/${episode.previousEpisodeId}`);
        }
      };
      window.comiciViewer.onFirst = () => {
        if (episode.firstEpisodeId) {
          router.push(`/episodes/${episode.firstEpisodeId}`);
        }
      };

      if (!window.comiciViewer.onStateChange) {
        window.comiciViewer.onStateChange = () => {
          const isOpen = document
            .getElementById("comici-viewer")
            ?.classList.contains("mode-nav-open");
          const $h = document.getElementById("xHeader");
          if (isOpen) {
            $h?.classList.remove(HEADER_HIDDEN_CLS);
          } else {
            $h?.classList.add(HEADER_HIDDEN_CLS);
          }
        };
      }
    }
  }, [viewerId, isViewerJSLoaded, isUserInfoLoading, sessionStatus, isFavedLoading, isLikeLoading]);

  let totopUrl = "/category/manga/";
  let totopLabel = "マンガトップへ";
  let totopDialogMessage = "最新話まで読み終わりました。<br>マンガトップへ戻ります";
  if (GLOBALS.HAS_GRAVURE && episode.series.category === "gravure") {
    totopUrl = "/category/gravure";
    totopLabel = "グラビアトップへ";
    totopDialogMessage = "最新話まで読み終わりました。<br>グラビアトップへ戻ります";
  }

  const apiDomain = "/api";
  return (
    <div
      className={
        "ep-main-viewer" +
        (glimpseBottom && glimpseBottom !== "1" ? " mode-ep-main-viewer-lg" : "") +
        (scrollVertical === "1" ? " mode-ep-main-viewer-v" : "")
      }
    >
      <div
        id="comici-viewer"
        data-comici-viewer-id={viewerId}
        data-mode-vertical={!episode.viewer?.isReadingHorizontal ? "1" : undefined}
        data-use-vh-change={episode.viewer?.isReadingHorizontal ? "1" : ""}
        data-api-domain={apiDomain}
        data-log-domain={GLOBALS.LOG_ENDPOINT_DOMAIN}
        data-log-tid={GLOBALS.LOG_TRACKING_ID}
        data-member-jwt={userInfo?.user?.id}
        data-member-id-token={session?.idToken}
        data-use-glimpse-bottom="1"
        data-skip-next-confirm="1"
        data-use-instruction="1"
        data-use-fullscreen="1"
        data-use-comici-style-lastpages="1"
        data-totop-url={totopUrl}
        data-totop-label={totopLabel}
        data-totop-dialog-message={totopDialogMessage}
        data-good-count-total={episode.summary.numLikes}
        data-good-count-user={like ? like.episodeLikes?.likeNumber : 0}
        data-comment-count="0"
        data-comment-hidden="1"
        data-v-image-max-width="700"
        data-totop-next-if-last="1"
        data-lastpage-ad-id={GLOBALS.VIEWER_AD_ID}
        data-h-direction={episode.viewer?.readingDirection || "rtl"}
        data-v-nav-direction={episode.viewer?.readingDirection || "rtl"}
        data-fan-letter-url={episode.fanLetterUrl}
        data-share-text={episode.shareMessage}
        data-comic-info="1"
        data-comic-info-api="js"
        data-comic-info-title={episode.viewer?.dataComicInfo?.dataComicTitle}
        data-series-id={episode.series.id}
        data-top-pr="js"
        data-pr="js"
        data-pr2="js"
        data-pr3="js"
        data-use-prev-next-js="1"
        data-next-episode-id={episode.nextEpisodeId}
        data-prev-episode-id={episode.previousEpisodeId}
        data-first-episode-id={episode.firstEpisodeId}
        data-disable-toc="1"
      ></div>
      {/* お気に入り機能はいずれ実装で
        data-fav-add={`/api/series/${episode.series.id}/favorite`}
        data-fav-add-method="POST"
        data-fav-remove={`/api/series/${episode.series.id}/favorite`}
        data-fav-remove-method="DELETE"
        data-fav-faved={faved?.favoriteStatus?.favorite ? "1" : ""}
        data-fav-count={episode.series.numFavorite}
      */}
      <Script
        type="text/javascript"
        //src="https://cdn-public.comici.jp/viewer/2.23.0/js/viewer.js"
        src="/js/viewer/viewer.js"
        onLoad={() => setIsViewerJSLoaded(true)}
      />
    </div>
  );
}
